import L from "leaflet";
import {
  BUILD_STEPS,
  CLARITY_CLASSES,
  MONTH_OPTIONS,
  NORMALIZED_FIELDS,
  PHASE_ONE_STATES,
  PUBLIC_SOURCES,
} from "./spec.js";
import { renderWaterClarityPanel } from "./ui.js";

const STATE_KEY = "hydrotools:water-clarity:v1";
const STATE_GEOJSON_URLS = {
  MN: "/data/water-clarity/mn-demo.geojson",
  WI: "/data/water-clarity/wi.geojson",
};

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const value = raw ? JSON.parse(raw) : {};
    const state = PHASE_ONE_STATES.includes(String(value.state || ""))
      ? String(value.state)
      : PHASE_ONE_STATES[0];
    const month = MONTH_OPTIONS.some((item) => item.value === String(value.month || ""))
      ? String(value.month)
      : "08";
    return { state, month };
  } catch {
    return { state: PHASE_ONE_STATES[0], month: "08" };
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

function monthLabel(monthValue) {
  return MONTH_OPTIONS.find((item) => item.value === monthValue)?.label || monthValue;
}

function sourcesForState(state) {
  return PUBLIC_SOURCES.filter((source) => source.state === "Regional" || source.state === state);
}

function summarizeState(state, month) {
  if (state === "MN") {
    return `Minnesota is a strong first state for ${monthLabel(month)} because field records can be paired with statewide satellite-derived clarity products.`;
  }
  if (state === "WI") {
    return `Wisconsin is well suited for ${monthLabel(month)} planning because SWIMS monitoring can be blended with statewide satellite Secchi products.`;
  }
  if (state === "IA") {
    return `Iowa is a good phase-one expansion target for ${monthLabel(month)} once the shared lake-month schema is stable.`;
  }
  if (state === "IL") {
    return `Illinois looks valuable, but the first pass should verify how much lake-level Secchi history is easy to normalize for ${monthLabel(month)}.`;
  }
  if (state === "MI") {
    return `Michigan should stay queued for phase two while the core workflow is proven on Minnesota, Wisconsin, and Iowa.`;
  }
  return `Historical clarity planning will use monthly summaries anchored to ${monthLabel(month)}.`;
}

export const waterClarityTool = {
  id: "water-clarity",
  name: "Water Clarity",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  const state = readState();
  const map = app.map.instance;
  let containerEl = null;
  let layerGroup = null;
  let globalLayerId = "";
  const demoDataByState = new Map();
  let loadPromise = null;
  let status = "Ready.";
  let loadState = {
    active: false,
    progress: 0,
    displayedProgress: 0,
    label: "Idle",
  };
  let syncToken = 0;
  let loadAnimationTimer = 0;

  function persist() {
    saveState(state);
  }

  function layerColor(suitability) {
    const key = String(suitability || "").toLowerCase();
    if (key === "excellent") return "#0f766e";
    if (key === "good") return "#65a30d";
    if (key === "fair") return "#d97706";
    return "#b91c1c";
  }

  function setLoadState(next) {
    loadState = {
      ...loadState,
      ...next,
    };
  }

  function stopLoadAnimation() {
    if (loadAnimationTimer) {
      window.clearInterval(loadAnimationTimer);
      loadAnimationTimer = 0;
    }
  }

  function ensureLoadAnimation() {
    if (loadAnimationTimer || !loadState.active) return;
    loadAnimationTimer = window.setInterval(() => {
      if (!loadState.active) {
        stopLoadAnimation();
        return;
      }
      const current = Number(loadState.displayedProgress) || 0;
      const target = Math.max(current, Number(loadState.progress) || 0);
      const remaining = target - current;
      const nextDisplayed = remaining > 0.5
        ? Math.min(target, current + Math.max(0.6, remaining * 0.18))
        : Math.min(target, current + 0.25);
      if (nextDisplayed !== current) {
        setLoadState({ displayedProgress: nextDisplayed });
        render();
      }
      if (!loadState.active && nextDisplayed >= target) {
        stopLoadAnimation();
      }
    }, 90);
  }

  function startLoad(label) {
    setLoadState({
      active: true,
      progress: 10,
      displayedProgress: Math.min(Number(loadState.displayedProgress) || 0, 6),
      label,
    });
    ensureLoadAnimation();
  }

  function advanceLoad(progress, label) {
    setLoadState({
      active: true,
      progress: Math.max(loadState.progress, progress),
      label: label || loadState.label,
    });
    ensureLoadAnimation();
  }

  function finishLoad(label) {
    setLoadState({
      active: false,
      progress: 100,
      displayedProgress: 100,
      label,
    });
    stopLoadAnimation();
  }

  function ensureDemoData({ force = false } = {}) {
    const url = STATE_GEOJSON_URLS[state.state];
    if (!url) return Promise.resolve(null);
    if (!force && demoDataByState.has(state.state)) return Promise.resolve(demoDataByState.get(state.state));
    if (loadPromise) return loadPromise;
    advanceLoad(25, `Requesting ${state.state} clarity data`);
    loadPromise = fetch(url, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`${state.state} clarity layer HTTP ${response.status}`);
        advanceLoad(45, `Downloading ${state.state} clarity data`);
        return response.json();
      })
      .then((json) => {
        advanceLoad(65, `Parsing ${state.state} clarity data`);
        demoDataByState.set(state.state, json);
        return json;
      })
      .finally(() => {
        loadPromise = null;
      });
    return loadPromise;
  }

  function filteredDemoFeatures() {
    const features = Array.isArray(demoDataByState.get(state.state)?.features) ? demoDataByState.get(state.state).features : [];
    return features.filter((feature) => String(feature?.properties?.month || "") === state.month);
  }

  function featuresForLake(feature) {
    const lakeId = String(feature?.properties?.lake_id || "");
    const features = Array.isArray(demoDataByState.get(state.state)?.features) ? demoDataByState.get(state.state).features : [];
    return features
      .filter((item) => String(item?.properties?.lake_id || "") === lakeId)
      .sort((a, b) => String(a?.properties?.month || "").localeCompare(String(b?.properties?.month || "")));
  }

  function secchiBarsSvg(lakeFeatures, selectedMonth) {
    const rows = lakeFeatures
      .map((item) => ({
        month: String(item?.properties?.month || ""),
        label: String(item?.properties?.month_label || item?.properties?.month || ""),
        value: Number(item?.properties?.secchi_median_m),
        suitability: String(item?.properties?.suitability_class || ""),
      }))
      .filter((item) => Number.isFinite(item.value));
    if (!rows.length) return `<div style="font-size:11px; color:#475569;">No monthly Secchi summary is available for this lake.</div>`;
    return secchiBarsSvgFromRows(rows, selectedMonth);
  }

  function secchiBarsSvgFromRows(rows, selectedMonth) {
    if (!rows.length) return `<div style="font-size:11px; color:#475569;">No monthly Secchi summary is available for this lake.</div>`;

    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    const axisMax = Math.max(1, Math.ceil(maxValue));
    const tickStep = axisMax <= 4 ? 1 : 2;
    const ticks = [];
    for (let value = 0; value <= axisMax; value += tickStep) ticks.push(value);
    if (ticks[ticks.length - 1] !== axisMax) ticks.push(axisMax);

    const width = 244;
    const height = 132;
    const left = 36;
    const right = 8;
    const top = 16;
    const bottom = 24;
    const innerWidth = width - left - right;
    const innerHeight = height - top - bottom;
    const barWidth = Math.max(10, Math.floor(innerWidth / Math.max(rows.length, 1)) - 6);
    const gap = rows.length > 1 ? (innerWidth - (rows.length * barWidth)) / (rows.length - 1) : 0;

    const bars = rows.map((item, index) => {
      const x = left + (index * (barWidth + gap));
      const barHeight = Math.max(1, (item.value / axisMax) * innerHeight);
      const y = top + innerHeight - barHeight;
      const isSelected = item.month === selectedMonth;
      const baseFill = layerColor(item.suitability);
      const fill = isSelected ? baseFill : `${baseFill}cc`;
      const textY = height - 8;
      return `
        <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barWidth}" height="${barHeight.toFixed(1)}" rx="2" ry="2" fill="${fill}">
          <title>${item.label}: ${item.value.toFixed(2)} m</title>
        </rect>
        <text x="${(x + (barWidth / 2)).toFixed(1)}" y="${textY}" text-anchor="middle" font-size="10" fill="#334155">${item.label.slice(0, 3)}</text>
      `;
    }).join("");

    const axis = ticks.map((tick) => {
      const y = top + innerHeight - ((tick / axisMax) * innerHeight);
      return `
        <line x1="${left}" y1="${y.toFixed(1)}" x2="${width - right}" y2="${y.toFixed(1)}" stroke="#e2e8f0" stroke-width="1" />
        <text x="${left - 4}" y="${(y + 3).toFixed(1)}" text-anchor="end" font-size="10" fill="#475569">${tick}</text>
      `;
    }).join("");

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Monthly Secchi depth bars">
        ${axis}
        <line x1="${left}" y1="${top + innerHeight}" x2="${width - right}" y2="${top + innerHeight}" stroke="#94a3b8" stroke-width="1" />
        <text x="8" y="${top + (innerHeight / 2)}" font-size="10" fill="#475569" transform="rotate(-90 8 ${top + (innerHeight / 2)})">Secchi (m)</text>
        ${bars}
      </svg>
    `;
  }

  function chartRowsForLake(feature) {
    return featuresForLake(feature)
      .map((item) => ({
        month: String(item?.properties?.month || ""),
        label: String(item?.properties?.month_label || item?.properties?.month || ""),
        value: Number(item?.properties?.secchi_median_m),
        suitability: String(item?.properties?.suitability_class || ""),
      }))
      .filter((item) => Number.isFinite(item.value));
  }

  function hexToRgb(hex) {
    const clean = String(hex || "").replace("#", "").trim();
    if (clean.length !== 6) return { r: 15, g: 118, b: 110 };
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16),
    };
  }

  function drawSecchiChartPdf(doc, rows, selectedMonth, x, y, width, height) {
    if (!rows.length) return 0;
    const axisMax = Math.max(1, Math.ceil(Math.max(...rows.map((item) => item.value), 1)));
    const tickStep = axisMax <= 4 ? 1 : 2;
    const ticks = [];
    for (let value = 0; value <= axisMax; value += tickStep) ticks.push(value);
    if (ticks[ticks.length - 1] !== axisMax) ticks.push(axisMax);

    const left = x + 34;
    const right = x + width - 8;
    const top = y + 8;
    const bottom = y + height - 24;
    const innerWidth = right - left;
    const innerHeight = bottom - top;
    const barWidth = Math.max(10, Math.floor(innerWidth / Math.max(rows.length, 1)) - 8);
    const gap = rows.length > 1 ? (innerWidth - (rows.length * barWidth)) / (rows.length - 1) : 0;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text("Secchi (m)", x + 8, y + (height / 2), { angle: 90 });

    ticks.forEach((tick) => {
      const yy = bottom - ((tick / axisMax) * innerHeight);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.6);
      doc.line(left, yy, right, yy);
      doc.setTextColor(71, 85, 105);
      doc.text(String(tick), left - 6, yy + 3, { align: "right" });
    });

    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.9);
    doc.line(left, bottom, right, bottom);

    rows.forEach((item, index) => {
      const xx = left + (index * (barWidth + gap));
      const barHeight = Math.max(1, (item.value / axisMax) * innerHeight);
      const yy = bottom - barHeight;
      const { r, g, b } = hexToRgb(layerColor(item.suitability));
      const alphaScale = item.month === selectedMonth ? 1 : 0.82;
      doc.setFillColor(
        Math.round(255 - ((255 - r) * alphaScale)),
        Math.round(255 - ((255 - g) * alphaScale)),
        Math.round(255 - ((255 - b) * alphaScale)),
      );
      doc.roundedRect(xx, yy, barWidth, barHeight, 2, 2, "F");
      doc.setTextColor(51, 65, 85);
      doc.text(item.label.slice(0, 3), xx + (barWidth / 2), y + height - 8, { align: "center" });
    });

    return height;
  }

  function popupHtml(feature) {
    const props = feature?.properties || {};
    const lakeFeatures = featuresForLake(feature);
    return `
      <div style="min-width:220px;">
        <div style="font-weight:700; margin-bottom:4px;">${String(props.lake_name || "Lake")}</div>
        <div><strong>Month:</strong> ${String(props.month_label || props.month || "--")}</div>
        <div><strong>Median Secchi:</strong> ${Number.isFinite(Number(props.secchi_median_m)) ? `${Number(props.secchi_median_m).toFixed(2)} m` : "--"}</div>
        <div><strong>Samples:</strong> ${Number(props.sample_count || 0)}</div>
        <div><strong>Confidence:</strong> ${String(props.confidence_class || "--")} (${Number(props.confidence_score || 0)})</div>
        <div><strong>Suitability:</strong> ${String(props.suitability_class || "--")}</div>
        <div><strong>Sources:</strong> ${String(props.source_mix || "--")}</div>
        <div><strong>Geometry:</strong> ${String(props.geometry_source || "--")}</div>
        <div style="margin-top:8px;">
          <div style="font-weight:600; margin-bottom:4px;">Monthly Secchi Median</div>
          ${secchiBarsSvg(lakeFeatures, String(props.month || ""))}
        </div>
        <div style="margin-top:8px;">
          <button type="button" class="btn wc-print-report">Print Report</button>
        </div>
      </div>
    `;
  }

  async function captureMapImage() {
    const html2canvas = (await import("html2canvas")).default;
    const popupPane = map.getPanes?.().popupPane || null;
    const canvas = await html2canvas(map.getContainer(), {
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      scale: Math.min(2, window.devicePixelRatio || 1),
      ignoreElements: (element) => popupPane ? popupPane.contains(element) : false,
    });
    return canvas.toDataURL("image/jpeg", 0.92);
  }

  async function waitForMapSettled({ timeoutMs = 8000, quietMs = 900 } = {}) {
    const started = Date.now();

    await new Promise((resolve) => {
      let done = false;
      let quietTimer = 0;
      let timeoutTimer = 0;

      const tileLayers = [];
      map.eachLayer((layer) => {
        if (typeof layer?.isLoading === "function" && typeof layer?.on === "function" && typeof layer?.off === "function") {
          tileLayers.push(layer);
        }
      });

      function cleanup() {
        tileLayers.forEach((layer) => {
          layer.off?.("loading", onBusy);
          layer.off?.("load", scheduleResolve);
          layer.off?.("tileloadstart", onBusy);
          layer.off?.("tileload", scheduleResolve);
          layer.off?.("tileerror", scheduleResolve);
        });
        map.off?.("movestart", onBusy);
        map.off?.("moveend", scheduleResolve);
        map.off?.("zoomstart", onBusy);
        map.off?.("zoomend", scheduleResolve);
        window.clearTimeout(quietTimer);
        window.clearTimeout(timeoutTimer);
      }

      function finish() {
        if (done) return;
        done = true;
        cleanup();
        resolve();
      }

      function onBusy() {
        window.clearTimeout(quietTimer);
      }

      function anythingLoading() {
        return tileLayers.some((layer) => {
          try { return !!layer.isLoading?.(); } catch { return false; }
        });
      }

      function scheduleResolve() {
        window.clearTimeout(quietTimer);
        if (anythingLoading()) return;
        quietTimer = window.setTimeout(() => {
          if (!anythingLoading()) finish();
        }, quietMs);
      }

      timeoutTimer = window.setTimeout(finish, Math.max(quietMs, timeoutMs - (Date.now() - started)));

      tileLayers.forEach((layer) => {
        layer.on?.("loading", onBusy);
        layer.on?.("load", scheduleResolve);
        layer.on?.("tileloadstart", onBusy);
        layer.on?.("tileload", scheduleResolve);
        layer.on?.("tileerror", scheduleResolve);
      });
      map.on?.("movestart", onBusy);
      map.on?.("moveend", scheduleResolve);
      map.on?.("zoomstart", onBusy);
      map.on?.("zoomend", scheduleResolve);

      scheduleResolve();
    });
  }

  async function generateLakeReport(feature, layer) {
    const props = feature?.properties || {};
    try {
      map.closePopup();
      const bounds = layer?.getBounds?.();
      if (bounds?.isValid?.()) {
        map.fitBounds(bounds.pad(0.4), { animate: false });
      }

      await waitForMapSettled();
      const mapImage = await captureMapImage();
      const chartRows = chartRowsForLake(feature);
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;
      let y = margin;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(String(props.lake_name || "Water Clarity Report"), margin, y);
      y += 18;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Month: ${String(props.month_label || props.month || "--")}`, margin, y); y += 14;
      doc.text(`Median Secchi: ${Number.isFinite(Number(props.secchi_median_m)) ? `${Number(props.secchi_median_m).toFixed(2)} m` : "--"}`, margin, y); y += 14;
      doc.text(`Samples: ${Number(props.sample_count || 0)}`, margin, y); y += 14;
      doc.text(`Confidence: ${String(props.confidence_class || "--")} (${Number(props.confidence_score || 0)})`, margin, y); y += 14;
      doc.text(`Suitability: ${String(props.suitability_class || "--")}`, margin, y); y += 14;
      doc.text(`Sources: ${String(props.source_mix || "--")}`, margin, y); y += 14;
      doc.text(`Geometry: ${String(props.geometry_source || "--")}`, margin, y); y += 20;

      if (chartRows.length) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Monthly Secchi Median", margin, y);
        y += 8;
        const chartW = pageW - (margin * 2);
        const chartH = 165;
        drawSecchiChartPdf(doc, chartRows, String(props.month || ""), margin, y, chartW, chartH);
        y += chartH + 18;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Lake Map", margin, y);
      y += 8;

      const imageW = pageW - (margin * 2);
      const imageH = Math.min(260, pageH - y - margin);
      doc.addImage(mapImage, "JPEG", margin, y, imageW, imageH, undefined, "FAST");

      doc.save(`${String(props.lake_name || "lake").replace(/[^\w.-]+/g, "_")}_${String(props.month || "report")}_water_clarity_report.pdf`);
      app.ui?.toast?.(`Saved report for ${String(props.lake_name || "lake")}`);
    } catch (error) {
      console.error("[WaterClarity] report failed", error);
      app.ui?.toast?.(error?.message || "Water clarity report failed");
    }
  }

  function clearDemoLayer() {
    if (globalLayerId) {
      try { app.services.layers.removeLayer(globalLayerId); } catch {}
      globalLayerId = "";
    }
    layerGroup = null;
  }

  function renderDemoLayer() {
    if (!STATE_GEOJSON_URLS[state.state]) {
      clearDemoLayer();
      status = `No generated clarity layer is available for ${state.state} yet.`;
      return;
    }
    const features = filteredDemoFeatures();
    if (!features.length) {
      clearDemoLayer();
      status = `No ${state.state} clarity summaries are available for ${monthLabel(state.month)}.`;
      return;
    }
    const nextLayerGroup = L.layerGroup([
      L.geoJSON({ type: "FeatureCollection", features }, {
        style: (feature) => ({
          color: "#0f172a",
          weight: 1.2,
          fillOpacity: 0.55,
          fillColor: layerColor(feature?.properties?.suitability_class),
        }),
        onEachFeature: (feature, layer) => {
          layer.bindPopup(popupHtml(feature), { maxWidth: 320 });
          layer.on("popupopen", () => {
            const popupEl = layer.getPopup()?.getElement?.();
            const btn = popupEl?.querySelector?.(".wc-print-report");
            if (!btn || btn.dataset.bound === "true") return;
            btn.dataset.bound = "true";
            btn.addEventListener("click", () => {
              generateLakeReport(feature, layer);
            });
          });
        },
      }),
    ]);
    clearDemoLayer();
    layerGroup = nextLayerGroup;
    globalLayerId = app.services.layers.addLayer("water-clarity", {
      name: `Water Clarity ${state.state} (${monthLabel(state.month)})`,
      kind: "overlay",
      group: "Water Clarity",
      type: "leaflet-group",
      leaflet: layerGroup,
      visible: true,
      opacity: 1,
      volatile: true,
      meta: {
        featureCount: features.length,
        source: `wqp-${String(state.state).toLowerCase()}`,
      },
    });
    status = `Loaded ${features.length} ${state.state} lake summaries for ${monthLabel(state.month)}.`;
  }

  async function syncDemoLayer() {
    const token = ++syncToken;
    try {
      status = STATE_GEOJSON_URLS[state.state]
        ? `Loading ${state.state} clarity layer for ${monthLabel(state.month)}...`
        : `No generated clarity layer is available for ${state.state} yet.`;
      startLoad(`Preparing ${state.state} ${monthLabel(state.month)} layer`);
      render();
      if (!STATE_GEOJSON_URLS[state.state]) {
        clearDemoLayer();
        finishLoad("No generated layer");
        render();
        return;
      }
      advanceLoad(18, `Checking ${state.state} layer availability`);
      await ensureDemoData({ force: true });
      if (token !== syncToken) return;
      advanceLoad(82, `Filtering ${monthLabel(state.month)} summaries`);
      renderDemoLayer();
      if (token !== syncToken) return;
      advanceLoad(94, `Rendering ${state.state} lake polygons`);
      finishLoad(`Ready for ${state.state} ${monthLabel(state.month)}`);
    } catch (error) {
      if (token !== syncToken) return;
      clearDemoLayer();
      status = `Water clarity demo layer failed to load: ${error?.message || String(error)}`;
      setLoadState({
        active: false,
        progress: 100,
        label: "Load failed",
      });
    }
    render();
  }

  function viewModel() {
    return {
      state: state.state,
      month: state.month,
      states: PHASE_ONE_STATES,
      months: MONTH_OPTIONS,
      classes: CLARITY_CLASSES,
      fields: NORMALIZED_FIELDS,
      buildSteps: BUILD_STEPS,
      sources: sourcesForState(state.state),
      status,
      isLoading: loadState.active,
      loadProgress: loadState.progress,
      displayedLoadProgress: loadState.displayedProgress,
      loadLabel: loadState.label,
      summary: summarizeState(state.state, state.month),
    };
  }

  function bind() {
    containerEl.querySelector("#wc-state")?.addEventListener("change", (event) => {
      state.state = String(event.target?.value || PHASE_ONE_STATES[0]);
      persist();
      syncDemoLayer();
    });
    containerEl.querySelector("#wc-month")?.addEventListener("change", (event) => {
      state.month = String(event.target?.value || "08");
      persist();
      syncDemoLayer();
    });
  }

  function render() {
    if (!containerEl) return;
    renderWaterClarityPanel(containerEl, viewModel());
    bind();
  }

  function mount(nextContainerEl) {
    containerEl = nextContainerEl;
    render();
    syncDemoLayer();
  }

  function destroy() {
    stopLoadAnimation();
    clearDemoLayer();
    containerEl = null;
  }

  return {
    mount,
    destroy,
  };
}
